import { useTranslation } from 'react-i18next';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { contactInfoFromEmployee } from '../../schemas/contact-info-schema';
import { Employee } from '../../types/employee.types';
import { ExternalLink, InfoCard, InfoRow } from './info-row';

const formatAddress = (parts: (string | undefined)[]) => parts.filter(Boolean).join(', ');

export interface ContactTabProps {
  employee: Employee;
  /** The employee looking at their own profile; others only see private details the employee shares. */
  isSelf: boolean;
}

/**
 * The person's contact details (`UserContactDetail`, plus the business email / phone): contact, personal
 * details, emergency contact, addresses, academic information, previous employment, social profiles and
 * references.
 */
export const ContactTab = ({ employee, isSelf }: ContactTabProps) => {
  const { t } = useTranslation();
  // Private details are shared only when the person chose to; a record that never said so keeps them hidden.
  const showPrivate = isSelf || employee.IsPrivateInfoVisible === true;

  const references = (contactInfoFromEmployee(employee).References ?? []).filter(
    (reference) => reference.Name || reference.Company || reference.Email || reference.Phone
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <InfoCard title={t('CONTACT_INFORMATION')}>
        <InfoRow label={t('EMAIL')} value={employee.Email} />
        <InfoRow label={t('PHONE')} value={employee.PhoneNumber} />
        {showPrivate && (
          <>
            <InfoRow label={t('EMAIL_PRIVATE')} value={employee.PrivateEmail} />
            <InfoRow label={t('PHONE_PRIVATE')} value={employee.PrivatePhoneNumber} />
          </>
        )}
      </InfoCard>

      <InfoCard title={t('PERSONAL_DETAILS')}>
        <InfoRow label={t('BLOOD_GROUP')} value={employee.BloodGroup} />
        <InfoRow label={t('FATHERS_NAME')} value={employee.FathersName} />
        <InfoRow label={t('MOTHERS_NAME')} value={employee.MothersName} />
        <InfoRow label={t('SPOUSE_NAME')} value={employee.SpouseName} />
        <InfoRow label={t('NID')} value={employee.Nid} />
        <InfoRow label={t('E_TIN')} value={employee.Etin} />
        <InfoRow label={t('PASSPORT_NO')} value={employee.PassportNo} />
        <InfoRow
          label={t('PASSPORT_EXPIRY_DATE')}
          value={formatDateOnly(employee.PassportExpiryDate)}
        />
        <InfoRow
          label={t('HOBBIES_INTERESTS')}
          value={(employee.HobbiesInterests ?? []).join(', ')}
        />
      </InfoCard>

      <InfoCard title={t('EMERGENCY_CONTACT')}>
        <InfoRow label={t('NAME')} value={employee.EmergencyContact?.Name} />
        <InfoRow label={t('RELATIONSHIP')} value={employee.EmergencyContact?.Relationship} />
        <InfoRow label={t('CONTACT_NO')} value={employee.EmergencyContact?.PhoneNumber} />
      </InfoCard>

      {showPrivate && (
        <>
          <InfoCard title={t('PRESENT_ADDRESS')}>
            <InfoRow
              label={t('PRESENT_ADDRESS')}
              value={formatAddress([
                employee.PresentAddress?.Address,
                employee.PresentAddress?.City,
                employee.PresentAddress?.Canton,
                employee.PresentAddress?.ZipCode,
                employee.PresentAddress?.Country,
              ])}
            />
          </InfoCard>
          <InfoCard title={t('PERMANENT_ADDRESS')}>
            <InfoRow
              label={t('PERMANENT_ADDRESS')}
              value={formatAddress([
                employee.PermanentAddress?.Address,
                employee.PermanentAddress?.City,
                employee.PermanentAddress?.Canton,
                employee.PermanentAddress?.ZipCode,
                employee.PermanentAddress?.Country,
              ])}
            />
          </InfoCard>
        </>
      )}

      <InfoCard title={t('ACADEMIC_INFORMATION')}>
        <InfoRow label={t('LATEST_ACADEMIC_TITLE')} value={employee.LatestAcademicTitle} />
        <InfoRow label={t('GRADUATION_INSTITUTE')} value={employee.GraduationInstitute} />
        <InfoRow label={t('POST_GRADUATION_INSTITUTE')} value={employee.PostGraduationInstitute} />
      </InfoCard>

      <InfoCard title={t('PREVIOUS_WORKING_INFORMATION')}>
        <InfoRow
          label={t('PREVIOUS_EXPERIENCE')}
          value={employee.PreviousExperience ? `${employee.PreviousExperience} ${t('YEARS')}` : ''}
        />
        <InfoRow label={t('PREVIOUS_ORGANIZATION')} value={employee.PreviousOrganization} />
      </InfoCard>

      <InfoCard title={t('SOCIAL_PROFILES')}>
        <InfoRow
          label={t('LINKEDIN_PROFILE')}
          value={<ExternalLink url={employee.LinkedInProfile} />}
        />
        <InfoRow
          label={t('GITHUB_PROFILE')}
          value={<ExternalLink url={employee.GithubProfile} />}
        />
        <InfoRow
          label={t('FACEBOOK_PROFILE')}
          value={<ExternalLink url={employee.FaceBookProfile} />}
        />
        <InfoRow
          label={t('WHATSAPP_PROFILE')}
          value={<ExternalLink url={employee.WhatsAppProfile} />}
        />
        <InfoRow
          label={t('INSTAGRAM_PROFILE')}
          value={<ExternalLink url={employee.InstagramProfile} />}
        />
        <InfoRow
          label={t('PROFESSIONAL_IMAGE_URL')}
          value={<ExternalLink url={employee.ProfessionalImageUrl} />}
        />
      </InfoCard>

      {references.map((reference, index) => (
        <InfoCard key={`${reference.Name}-${index}`} title={`${t('REFERENCE')} ${index + 1}`}>
          <InfoRow label={t('NAME')} value={reference.Name} />
          <InfoRow label={t('DESIGNATION')} value={reference.Designation} />
          <InfoRow label={t('COMPANY')} value={reference.Company} />
          <InfoRow label={t('EMAIL')} value={reference.Email} />
          <InfoRow label={t('PHONE')} value={reference.Phone} />
        </InfoCard>
      ))}
    </div>
  );
};
